import * as React from "react";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import {
  getIssueTableSelectedIds,
  getNumberOfIssueTableSelectedIds,
} from "@adsk/acc-issues-core/state/redux/ducks/issueTableSelection";
import {
  useIssuesFetchParams,
  useNumberOfFilters,
} from "@adsk/acc-issues-core/state/issuesFetchParams/useIssueFetchParams";
import { getTotalNumberOfIssues } from "@adsk/acc-issues-core/state/redux/ducks/issuesCollection";
import { useTranslations } from "@adsk/acc-issues-translations";
import { ExportIcon } from "@adsk/alloy-react-icon";
import Menu, {
  DropdownButton,
  MenuItem,
  MenuItemType,
} from "@adsk/alloy-react-menu";
import { BcfModal } from "@adsk/acc-issues-list";
import Tooltip from "@adsk/alloy-react-tooltip/es/Tooltip";
import { ComponentProps, ReactNode, useContext } from "react";
import { CSSObject } from "styled-components";
import { DeletedIssuesSpaceContext } from "@adsk/acc-issues-core/providers/DeletedIssuesSpaceContext";
import { BCF_EXPORT_ISSUES_LIMIT } from "@adsk/acc-issues-common/constants/bcf";
import { useNumOfIssuesToExport } from "@adsk/acc-issues-common/hooks/export/useNumOfIssuesToExport";
import { useIsModelAggregateInViewer } from "@adsk/acc-issues-core/providers/IsModelAggregateInViewerContext";

const CustomMenuItem = (
  props: ComponentProps<typeof MenuItem> & {
    tooltip?: string;
  }
) => (
  <Tooltip content={props.tooltip} placement={Tooltip.PLACEMENTS.RIGHT}>
    <MenuItem
      {...props}
      style={{
        ...(props.style as CSSObject),
      }}
    />
  </Tooltip>
);

type ExportOption = MenuItemType<string> & { tooltip?: ReactNode };

export const IssueExportButton = ({
  onExportButtonClicked,
}: {
  onExportButtonClicked: () => void;
}) => {
  const numberOfFilters = useNumberOfFilters({
    includeSearchFilterInCount: true,
  });
  const numberOfIssueTableSelectedIds = useSelector(
    getNumberOfIssueTableSelectedIds
  );
  const totalIssues = useSelector(getTotalNumberOfIssues);
  const { T } = useTranslations();
  const isModelAggregateInViewer = useIsModelAggregateInViewer();
  const disableBCFButton =
    useNumOfIssuesToExport() > BCF_EXPORT_ISSUES_LIMIT ||
    isModelAggregateInViewer;
  const disableBCFTooltip = isModelAggregateInViewer
    ? T.translate("reports.bcf.tooltipIsModelAggregateInViewerMessage")
    : T.translate("reports.bcf.tooltipMessage", {
        issueCountLimit: BCF_EXPORT_ISSUES_LIMIT,
      });
  const isDeletedSpaceActive = useContext(DeletedIssuesSpaceContext);
  const exportOptions: ExportOption[] = [
    {
      key: "exportReport",
      label: T.translate("reports.exportButton.exportReport"),
      onClick: onExportButtonClicked,
    },
  ];
  if (!isDeletedSpaceActive) {
    exportOptions.push({
      key: "exportBcf",
      label: T.translate("actions.exportAsBcf"),
      tooltip: disableBCFButton ? disableBCFTooltip : undefined,
      onClick: () => setOpenBcfModal(true),
      disabled: disableBCFButton,
    });
  }
  const [openBcfModal, setOpenBcfModal] = React.useState<boolean>(false);
  const issueTableSelectedIds = useSelector(getIssueTableSelectedIds);
  const { value: issuesFetchParams } = useIssuesFetchParams();
  const exportParams =
    numberOfIssueTableSelectedIds > 0
      ? { id: issueTableSelectedIds }
      : issuesFetchParams?.filterBy;
  return (
    <div data-testid="issue-export-button">
      <DropdownButton
        disabled={totalIssues === 0}
        renderIcon={p => <ExportIcon {...p} />}
        options={exportOptions}
        renderContent={props => <Menu {...props} renderItem={CustomMenuItem} />}
      >
        {numberOfIssueTableSelectedIds > 0
          ? T.translate("reports.exportButton.exportSelected", {
              numberOfIssueTableSelectedIds,
            })
          : numberOfFilters > 0
          ? T.translate("reports.exportButton.exportFiltered", {
              totalIssues,
            })
          : T.translate("reports.exportButton.exportAll")}
      </DropdownButton>
      {exportParams && (
        <BcfModal
          isOpen={openBcfModal}
          closeReportModal={() => setOpenBcfModal(false)}
          exportParams={exportParams}
        />
      )}
    </div>
  );
};
